/** 편집·확정·내보내기가 거절됐을 때 셀러에게 할 말.
 *
 * ## 왜 `catch {}` 한 줄로 두면 안 되는가
 *
 * 서버는 이 경로들에서 **사유를 코드로 갈라 준다**(`NOT_EXPORTABLE` · `JOB_DISCARDED` ·
 * `ALREADY_PROCESSING` · `IMAGE_SET_MISMATCH` …). 갈라 준 이유는 하나다 — **기다리면 풀리는
 * 것과 기다려도 안 풀리는 것이 다르기** 때문이다.
 *
 * ⛔ **그래서 "잠시 뒤 다시 해 주세요"를 기본 문구로 쓰면 안 된다.** 그 문장은 `NOT_EXPORTABLE`
 * (만든 장이 없다)이나 `BACKGROUND_NOT_ALLOWED`(아직 고를 수 없는 색)에서 **거짓말**이다.
 * 셀러는 풀리지 않을 것을 기다리고, 몇 번 더 누르고, 그래도 안 되면 제품을 의심한다.
 * `apps/web/src/lib/api.ts` 머리말의 규약(**화면 문구는 status·code 로 고른다 — 서버 메시지를
 * 그대로 쓰지 않는다**)이 이 자리를 가리킨다.
 *
 * **서버 메시지(`error`)를 그대로 띄우지 않는 이유**는 그쪽이 영어이고 구현 용어이며, 바뀔 때
 * 화면 문구가 함께 바뀌어 버리기 때문이다. 코드는 계약이고 문장은 우리 것이다.
 *
 * ⚠️ **앱도 같은 코드를 받는다.** 지금은 웹만 쓰지만 앱이 이 라우트들을 부르는 날 같은 표가
 * 한 벌 더 생긴다. 그때는 이 파일을 `packages/shared` 로 올린다 — 두 벌이 갈리면 같은 거절이
 * 앱과 웹에서 다른 말을 한다(`failure-messages.ts` 가 이미 그 자리에 있다).
 */

import { PUBLISH_REJECTION_TEXT } from "@makit/shared";

import { ApiError } from "./api";

/** 그 거절이 **기다리면 풀리는가**. 화면이 「다시 해 보세요」를 붙일지 정하는 축이다. */
export interface RejectionMessage {
  /** 셀러에게 보이는 한 문장. 마침표로 끝난다. */
  text: string;
  /** 같은 동작을 다시 걸면 될 수 있는가. `false` 면 화면이 재시도를 권하지 않는다. */
  retryable: boolean;
}

const BY_CODE: Record<string, RejectionMessage> = {
  // 도는 중이라 잠긴 것이다. **이것만이 진짜 "잠시 뒤"** 다.
  ALREADY_PROCESSING: { text: "지금 만드는 중이라 잠시 뒤에 됩니다.", retryable: true },
  // 번호 경합. 다시 걸면 대개 통과한다.
  VERSION_CONFLICT: { text: "판 번호가 겹쳤습니다. 다시 눌러 주세요.", retryable: true },

  // ── 기다려도 안 풀리는 것들 ───────────────────────────────────────────────
  //
  // ⭐ **아래 둘은 뜻이 정반대라 뭉개면 셀러가 할 일이 반대로 읽힌다**(`13.1` O-73).
  //    `NOT_CONFIRMED` 는 **셀러가 하나도 안 골랐다** — 다시 고르면 된다.
  //    `NO_RESULT` 는 **골랐는데 줄 수 있는 것이 없다** — 다시 골라도 소용없다.
  //    ⛔ **문장을 여기서 새로 적지 마라.** 정본은 `@makit/shared` 의
  //    `PUBLISH_REJECTION_TEXT` 이고 **앱 `A-34` 가 같은 상수를 읽는다.** 두 벌이 되면 같은
  //    거절이 폰과 PC 에서 다른 말을 한다(이 파일 머리말의 ⚠️ 가 가리킨 자리다).
  NOT_CONFIRMED: { text: PUBLISH_REJECTION_TEXT.NOT_CONFIRMED, retryable: false },
  NO_RESULT: { text: PUBLISH_REJECTION_TEXT.NO_RESULT, retryable: false },
  NOT_EXPORTABLE: { text: "아직 내보낼 것이 없습니다. 결과가 나온 뒤에 됩니다.", retryable: false },
  JOB_DISCARDED: { text: "이 작업의 원본 사진이 없어져 더 진행할 수 없습니다.", retryable: false },
  // 화면이 들고 있던 사진 목록이 서버와 갈렸다. 다시 걸어도 같은 목록을 보낸다 —
  // 셀러가 할 일은 **화면을 새로 여는 것**이다.
  IMAGE_SET_MISMATCH: {
    text: "사진 목록이 바뀌었습니다. 화면을 새로 고친 뒤 다시 해 주세요.",
    retryable: false,
  },
  BACKGROUND_NOT_ALLOWED: { text: "아직 고를 수 없는 배경입니다.", retryable: false },
  PRODUCT_NAME_TOO_LONG: { text: "상품명이 너무 깁니다.", retryable: false },
  PRODUCT_NAME_EXISTS: { text: "다른 곳에서 저장한 상품명이 있습니다. 새로고침해 확인해 주세요.", retryable: false },
};

/** 거절을 셀러의 말로. **모르는 코드는 지어내지 않는다** — 서버가 새 코드를 내려도 화면이
 *  깨지면 안 되고, 그때 할 수 있는 정직한 말은 "안 됐다"까지다. */
export function rejectionMessage(err: unknown, fallbackAction: string): RejectionMessage {
  if (!(err instanceof ApiError)) {
    return { text: `${fallbackAction}지 못했습니다. 다시 해 주세요.`, retryable: true };
  }
  // 코드가 먼저다. 같은 상태 번호(409)에 사유가 여럿이라 status 로는 못 가른다.
  const known = err.code ? BY_CODE[err.code] : undefined;
  if (known) return known;

  // 연결이 끊긴 경우다(`ApiError` 가 status 0 으로 만든다). **기다리면 풀리는 쪽이다.**
  if (err.status === 0) {
    return { text: `연결이 끊겨 ${fallbackAction}지 못했습니다. 다시 해 주세요.`, retryable: true };
  }
  // 401 은 `notifyUnauthorized` 가 이미 게이트를 흔들어 로그인으로 보낸다. 여기서 문장을
  // 띄워도 화면이 곧 바뀌므로 짧게만 적는다.
  if (err.status === 401) {
    return { text: "로그인이 끝났습니다.", retryable: false };
  }
  if (err.status === 404) {
    return { text: "이 작업을 찾지 못했습니다.", retryable: false };
  }
  // 5xx 와 그 밖. 우리 쪽이 답을 못 준 것이라 다시 해 볼 만하다.
  return { text: `${fallbackAction}지 못했습니다. 다시 해 주세요.`, retryable: true };
}

/** 토스트 한 줄.
 *
 * ⛔ **문장을 여기서 조립하지 않는다.** 위 표의 문장은 **그 자체로 완성돼 있고**, 재시도를
 * 권하는 말도 문장 안에 이미 들어 있다(`다시 눌러 주세요` · `잠시 뒤에 됩니다`). 꼬리말을
 * 덧붙이면 「다시 눌러 주세요. 다시 해 주세요.」처럼 같은 말이 두 번 붙고, 그것을 문자열
 * 치환으로 다시 떼어내는 코드가 생긴다. **`retryable` 은 문장을 만드는 값이 아니라 화면이
 * 버튼을 살릴지 정하는 값이다.** */
export function rejectionToast(err: unknown, fallbackAction: string): string {
  return rejectionMessage(err, fallbackAction).text;
}
